/**
 * Version Metadata Editor
 * Manages advanced version metadata including custom labels, tags, and classifications
 */

import React, { useState, useEffect } from 'react';
import { DocumentVersion } from '../../../shared/document/types';
import { DocumentService } from '../../../shared/document/DocumentService';
import { 
  TagIcon,
  BookmarkIcon,
  DocumentTextIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface VersionMetadataEditorProps {
  documentId: string;
  version: DocumentVersion;
  onMetadataUpdate?: (metadata: VersionMetadata) => void;
  onClose?: () => void;
}

interface VersionMetadata {
  labels: string[];
  tags: string[];
  category: string;
  classification: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'archived' | 'deprecated';
  customFields: Record<string, string>;
  relationships: VersionRelationship[];
  notes: string;
}

interface VersionRelationship {
  type: 'depends_on' | 'supersedes' | 'related_to' | 'conflicts_with';
  targetVersionId: string;
  targetDocumentId: string;
  description: string;
}

const CATEGORIES = [
  'Contract',
  'Motion',
  'Brief',
  'Memo',
  'Letter',
  'Agreement',
  'Policy',
  'Procedure',
  'Template',
  'Other',
];

const CLASSIFICATIONS = [
  'Public',
  'Internal',
  'Confidential',
  'Attorney-Client Privileged',
  'Work Product',
  'Sensitive',
];

const RELATIONSHIP_TYPES = [
  { value: 'depends_on', label: 'Depends On' },
  { value: 'supersedes', label: 'Supersedes' },
  { value: 'related_to', label: 'Related To' },
  { value: 'conflicts_with', label: 'Conflicts With' },
];

export function VersionMetadataEditor({
  documentId,
  version,
  onMetadataUpdate,
  onClose,
}: VersionMetadataEditorProps): React.JSX.Element {
  const [metadata, setMetadata] = useState<VersionMetadata>({
    labels: [],
    tags: [],
    category: '',
    classification: 'Internal',
    priority: 'medium',
    status: 'active',
    customFields: {},
    relationships: [],
    notes: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newCustomField, setNewCustomField] = useState({ key: '', value: '' });
  const [showRelationshipForm, setShowRelationshipForm] = useState(false);
  const [newRelationship, setNewRelationship] = useState<Partial<VersionRelationship>>({
    type: 'related_to',
    description: '',
  });

  useEffect(() => {
    loadMetadata();
  }, [version]);

  const loadMetadata = async () => {
    try {
      const document = await DocumentService.getDocumentById(documentId);
      const versionMetadata = document.metadata?.versionMetadata?.[version.id] || {};
      setMetadata({
        ...metadata,
        ...versionMetadata,
      });
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const saveMetadata = async () => {
    setIsSaving(true);
    try {
      await DocumentService.saveLexicalContent(documentId, '', {
        versionMetadata: {
          [version.id]: metadata,
        },
      });
      
      onMetadataUpdate?.(metadata);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save metadata:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const addLabel = () => {
    if (newLabel.trim() && !metadata.labels.includes(newLabel.trim())) {
      setMetadata(prev => ({
        ...prev,
        labels: [...prev.labels, newLabel.trim()],
      }));
      setNewLabel('');
    }
  };

  const removeLabel = (label: string) => {
    setMetadata(prev => ({
      ...prev,
      labels: prev.labels.filter(l => l !== label),
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !metadata.tags.includes(newTag.trim())) {
      setMetadata(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setMetadata(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  const addCustomField = () => {
    if (newCustomField.key.trim() && newCustomField.value.trim()) {
      setMetadata(prev => ({
        ...prev,
        customFields: {
          ...prev.customFields,
          [newCustomField.key.trim()]: newCustomField.value.trim(),
        },
      }));
      setNewCustomField({ key: '', value: '' });
    }
  };

  const removeCustomField = (key: string) => {
    setMetadata(prev => {
      const newCustomFields = { ...prev.customFields };
      delete newCustomFields[key];
      return {
        ...prev,
        customFields: newCustomFields,
      };
    });
  };

  const addRelationship = () => {
    if (newRelationship.type && newRelationship.description) {
      setMetadata(prev => ({
        ...prev,
        relationships: [
          ...prev.relationships,
          {
            type: newRelationship.type!,
            targetVersionId: newRelationship.targetVersionId || '',
            targetDocumentId: newRelationship.targetDocumentId || '',
            description: newRelationship.description!,
          },
        ],
      }));
      setNewRelationship({ type: 'related_to', description: '' });
      setShowRelationshipForm(false);
    }
  };

  const removeRelationship = (index: number) => {
    setMetadata(prev => ({
      ...prev,
      relationships: prev.relationships.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Version Metadata
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Version {version.version} - {new Date(version.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={saveMetadata}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                >
                  Edit
                </button>
                {onClose && (
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Basic Information */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Basic Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <select
                value={metadata.category}
                onChange={(e) => setMetadata(prev => ({ ...prev, category: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
              >
                <option value="">Select category</option>
                {CATEGORIES.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Classification
              </label>
              <select
                value={metadata.classification}
                onChange={(e) => setMetadata(prev => ({ ...prev, classification: e.target.value }))}
                disabled={!isEditing}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
              >
                {CLASSIFICATIONS.map(classification => (
                  <option key={classification} value={classification}>{classification}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <select
                value={metadata.priority}
                onChange={(e) => setMetadata(prev => ({ ...prev, priority: e.target.value as any }))}
                disabled={!isEditing}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={metadata.status}
                onChange={(e) => setMetadata(prev => ({ ...prev, status: e.target.value as any }))}
                disabled={!isEditing}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>
          </div>
        </div>

        {/* Labels */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Labels
          </h4>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {metadata.labels.map((label, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                >
                  <TagIcon className="h-3 w-3 mr-1" />
                  {label}
                  {isEditing && (
                    <button
                      onClick={() => removeLabel(label)}
                      className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {isEditing && (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Add label..."
                  className="flex-1 px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <button
                  onClick={addLabel}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Tags
          </h4>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {metadata.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                >
                  <BookmarkIcon className="h-3 w-3 mr-1" />
                  {tag}
                  {isEditing && (
                    <button
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {isEditing && (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add tag..."
                  className="flex-1 px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <button
                  onClick={addTag}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Custom Fields */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Custom Fields
          </h4>
          <div className="space-y-2">
            {Object.entries(metadata.customFields).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{key}:</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">{value}</span>
                </div>
                {isEditing && (
                  <button
                    onClick={() => removeCustomField(key)}
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {isEditing && (
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={newCustomField.key}
                  onChange={(e) => setNewCustomField(prev => ({ ...prev, key: e.target.value }))}
                  placeholder="Field name"
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newCustomField.value}
                    onChange={(e) => setNewCustomField(prev => ({ ...prev, value: e.target.value }))}
                    placeholder="Field value"
                    className="flex-1 px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <button
                    onClick={addCustomField}
                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Relationships */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Version Relationships
          </h4>
          <div className="space-y-2">
            {metadata.relationships.map((relationship, index) => (
              <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                      {relationship.type.replace('_', ' ')}:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                      {relationship.description}
                    </span>
                  </div>
                  {isEditing && (
                    <button
                      onClick={() => removeRelationship(index)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {isEditing && (
              <button
                onClick={() => setShowRelationshipForm(true)}
                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
              >
                <PlusIcon className="h-4 w-4 inline mr-2" />
                Add Relationship
              </button>
            )}
          </div>
        </div>

        {/* Notes */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Notes
          </h4>
          <textarea
            value={metadata.notes}
            onChange={(e) => setMetadata(prev => ({ ...prev, notes: e.target.value }))}
            disabled={!isEditing}
            placeholder="Add notes about this version..."
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
            rows={3}
          />
        </div>
      </div>

      {/* Relationship Form Modal */}
      {showRelationshipForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Add Version Relationship
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Relationship Type
                    </label>
                    <select
                      value={newRelationship.type}
                      onChange={(e) => setNewRelationship(prev => ({ ...prev, type: e.target.value as any }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {RELATIONSHIP_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={newRelationship.description}
                      onChange={(e) => setNewRelationship(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe the relationship..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={addRelationship}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Add Relationship
                </button>
                <button
                  onClick={() => setShowRelationshipForm(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VersionMetadataEditor; 